import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  congesApi,
  type CategorieProposee,
  type TypeAbsence,
  type VerdictSoumission,
} from '../../services/conges-api';
import { BoutonPrincipal, BoutonSecondaire, Case, Champ, Saisie, Selection } from '../ndf/ui/Champ';
import { AideType } from './AideType';
import { lireMecanismes } from '../../lib/champs-type-absence';
import { erreurGlobale, messageRefusPiece } from '../ndf/ui/erreurs-champs';
import { refusDuJustificatif } from '../../lib/piece-jointe';
import { MessageErreur, MessageVide } from '../ndf/ui/MessageEtat';
import { useToast } from '../../context/ToastContext';

/**
 * Le formulaire de demande d'absence (specs §D1), EXTRAIT pour être posé
 * partout où l'on pose une absence.
 *
 * Deux portes d'entrée, un seul formulaire : le bouton « Poser une absence »
 * l'ouvre sur une page, le calendrier l'ouvre sous le mois, dates déjà
 * remplies. En tenir deux versions aurait garanti qu'une règle ajoutée à l'une
 * manque à l'autre.
 *
 * LE DÉCOMPTE ET LES CONTRÔLES VIENNENT DU SERVEUR. Le calendrier des jours
 * fériés est administrable : un calcul refait dans le navigateur finirait par
 * annoncer un nombre de jours que le serveur refuserait ensuite.
 */

function jours(v: number): string {
  return v.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

const dateFr = (iso: string) => iso.split('-').reverse().join('/');

export interface ValeursInitiales {
  debut?: string;
  fin?: string;
  demi?: string;
}

export function FormulaireDemande({
  initial,
  onTermine,
  onAnnuler,
  compact,
}: {
  initial?: ValeursInitiales;
  /** Appelé après création : la page décide où aller ensuite. */
  onTermine: (id: string | null) => void;
  onAnnuler: () => void;
  /** Posé sous un calendrier : on resserre les marges, pas le contenu. */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [categorieId, setCategorieId] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [debut, setDebut] = useState(initial?.debut ?? '');
  const [fin, setFin] = useState(initial?.fin ?? initial?.debut ?? '');
  /**
   * UNE demande porte UNE information de demi-journée, ou aucune.
   *
   * Le formulaire offrait deux listes — « à partir de » et « jusqu'à » — même
   * sur un jour unique, où les deux bornes désignent la MÊME journée. On
   * pouvait donc saisir « du matin à l'après-midi », qui se lit comme une
   * journée entière et se décomptait 0,5 (mesuré le 30/09/2026).
   *
   * Règle posée par Valérie, qui est déjà la pratique : une demi-journée est
   * TOUJOURS une demande à part ; une demande de plusieurs jours n'a que des
   * journées entières. « Je pars mardi midi et je reviens lundi » se pose en
   * deux demandes : la demi-journée du mardi après-midi, puis les jours entiers.
   */
  const [estDemiJournee, setEstDemiJournee] = useState(
    initial?.demi === 'matin' || initial?.demi === 'apres_midi',
  );
  const [moitie, setMoitie] = useState<'matin' | 'apres_midi'>(
    initial?.demi === 'apres_midi' ? 'apres_midi' : 'matin',
  );
  const [description, setDescription] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [piece, setPiece] = useState<{ filename: string; contenu_base64: string; mime: string } | null>(
    null,
  );

  // Le calendrier peut changer la sélection sans démonter le formulaire : les
  // dates doivent suivre, sinon on poserait la période précédente.
  useEffect(() => {
    if (initial?.debut) setDebut(initial.debut);
    if (initial?.fin ?? initial?.debut) setFin(initial.fin ?? initial.debut!);
    // « journée entière » DOIT décocher la case. Sans ce cas, le raccourci du
    // calendrier s'allumait sur « Journée entière » pendant que le formulaire
    // envoyait encore « matin » : 0,5 j décompté au lieu de 1 (régression
    // relevée à l'audit du 30/09/2026).
    if (initial?.demi === 'matin' || initial?.demi === 'apres_midi') {
      setEstDemiJournee(true);
      setMoitie(initial.demi);
    } else if (initial?.demi === 'journee') {
      setEstDemiJournee(false);
    }
  }, [initial?.debut, initial?.fin, initial?.demi]);

  const {
    data: proposables,
    isLoading: chargementTypes,
    error: erreurTypes,
  } = useQuery({
    queryKey: ['conges', 'proposables'],
    queryFn: () => congesApi.typesAbsence.proposables(),
  });
  const { data: parametres } = useQuery({
    queryKey: ['conges', 'parametres'],
    queryFn: () => congesApi.parametres.liste(),
    retry: false,
  });

  const categories: CategorieProposee[] = proposables?.categories ?? [];
  const categorie = categories.find((c) => c.categorie.id === categorieId) ?? null;
  const type: TypeAbsence | null = categorie?.types.find((t) => t.id === typeId) ?? null;
  const mecanismes = type ? lireMecanismes(type.mecanismes) : [];

  useEffect(() => {
    if (!categorieId && categories.length === 1) setCategorieId(categories[0]!.categorie.id);
  }, [categories, categorieId]);

  /** Un seul jour : c'est la seule situation où une demi-journée a un sens. */
  const surUnSeulJour = Boolean(debut) && (!fin || fin === debut);
  /**
   * Les deux bornes reçoivent la MÊME valeur, ou « journée » partout. C'est ce
   * qui rend « du matin à l'après-midi » inexprimable : le modèle de données
   * garde ses deux bornes (le découpage automatique d'une demande qui dépasse
   * le solde s'en sert), mais le formulaire n'en produit plus qu'une.
   */
  const demi = surUnSeulJour && estDemiJournee ? moitie : 'journee';

  const corps = useMemo(
    () => ({
      type_absence_id: typeId ?? '',
      date_debut: debut,
      date_fin: fin || debut,
      debut_demi: demi,
      fin_demi: demi,
      description: description || null,
      commentaire: commentaire || null,
    }),
    [typeId, debut, fin, demi, description, commentaire],
  );

  const { data: verdict } = useQuery<VerdictSoumission | null>({
    queryKey: ['conges', 'verifier', corps, Boolean(piece)],
    queryFn: () =>
      typeId && debut
        ? congesApi.demandes.verifier({ ...corps, a_justificatif: Boolean(piece) })
        : Promise.resolve(null),
    enabled: Boolean(typeId && debut),
  });

  const envoyer = useMutation({
    mutationFn: () => congesApi.demandes.creer({ ...corps, justificatif: piece ?? undefined }),
    onSuccess: (r) => {
      toast.success(
        r.alertes.length
          ? 'Demande envoyée. Le valideur a été prévenu du dépassement.'
          : 'Demande envoyée.',
      );
      qc.invalidateQueries({ queryKey: ['conges'] });
      onTermine(r.id);
    },
    onError: (e) => toast.error(messageRefusPiece(e) ?? erreurGlobale(e) ?? 'L’envoi a échoué.'),
  });

  const dedoubler = useMutation({
    mutationFn: () => congesApi.demandes.dedoubler(corps),
    onSuccess: () => {
      toast.success('Deux demandes créées : la part acquise et le congé sans solde.');
      qc.invalidateQueries({ queryKey: ['conges'] });
      onTermine(null);
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'Le découpage a échoué.'),
  });

  const mention = parametres?.parametres.find((p) => p.key === 'conges_mention_conge_principal');

  /**
   * Le dédoublement REMPLACE le message de blocage, mais seulement quand il a
   * quelque chose à proposer.
   *
   * Il n'en a pas toujours : à solde projeté nul ou négatif — des droits
   * épuisés — il n'y a rien à découper. On masquait pourtant le message dans ce
   * cas, et le bouton restait grisé sans un mot d'explication.
   */
  const dedoublementPropose = Boolean(verdict?.anticipation && verdict.dedoublement);
  const seulBlocageSolde = Boolean(
    verdict?.anticipation && verdict.blocages.length === 1 && dedoublementPropose,
  );

  const peutEnvoyer = Boolean(typeId && debut && verdict && verdict.blocages.length === 0);

  /**
   * Pourquoi le bouton est grisé, TOUJOURS dit.
   *
   * Un bouton désactivé sans raison est une impasse : on ne sait ni ce qui
   * manque, ni si l'on doit attendre. C'est la règle générale, elle vaut pour
   * chaque contrôle et pas seulement pour le solde.
   */
  const raisonBlocage = (() => {
    if (peutEnvoyer) return null;
    if (!categorieId) return 'Choisissez une catégorie.';
    if (!typeId) return 'Choisissez un type d’absence.';
    if (!debut) return 'Indiquez la date de début.';
    if (!verdict) return 'Vérification en cours…';
    if (verdict.blocages.length > 0) return verdict.blocages.join(' ');
    return null;
  })();

  const lireFichier = (f: File, champ?: HTMLInputElement | null) => {
    const refus = refusDuJustificatif(f);
    if (refus) {
      toast.error(refus);
      // Le sélecteur est VIDÉ : sans cela, rechoisir le MÊME fichier ne
      // déclenche plus rien — pas même le message —, et l'écran paraît muet
      // alors qu'il vient justement d'apprendre à parler.
      if (champ) champ.value = '';
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const brut = String(lecteur.result ?? '');
      setPiece({ filename: f.name, contenu_base64: brut.split(',')[1] ?? '', mime: f.type });
    };
    lecteur.readAsDataURL(f);
  };

  // TROIS ÉTATS AVANT LE FORMULAIRE. Sans eux, l'écran rendait un sélecteur de
  // catégories VIDE et muet : ni chargement, ni erreur, ni explication. C'est
  // l'état qu'on rencontre juste après une mise en service, tant que les
  // référentiels ne sont pas amorcés — le moment précis où l'on a le plus
  // besoin de savoir ce qui manque.
  //
  // CHACUN GARDE « FERMER ». Sous le calendrier, ce bouton est la SEULE façon de
  // refermer le panneau : les trois premiers états, écrits sans lui, laissaient
  // l'utilisateur devant un message qu'il ne pouvait plus quitter — exactement
  // dans la situation d'après mise en service qu'ils servent à expliquer.
  const etatAvantFormulaire = (contenu: React.ReactNode) => (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {contenu}
      <BoutonSecondaire type="button" onClick={onAnnuler}>
        Fermer
      </BoutonSecondaire>
    </div>
  );

  if (chargementTypes) {
    return etatAvantFormulaire(
      <p className="p-1 text-sm text-nuit/60">Chargement des types d’absence…</p>,
    );
  }
  if (erreurTypes) {
    return etatAvantFormulaire(<MessageErreur erreur={erreurTypes} />);
  }
  if (categories.length === 0) {
    return etatAvantFormulaire(
      <MessageVide>
        Aucun type d’absence ne vous est proposé. Si cela vous semble anormal, signalez-le à la RH :
        le référentiel des absences n’a peut-être pas encore été amorcé.
      </MessageVide>,
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ label="Catégorie" requis>
          <Selection
            value={categorieId ?? ''}
            onChange={(e) => {
              setCategorieId(e.target.value || null);
              setTypeId(null);
            }}
          >
            <option value="">Choisir</option>
            {categories.map((c) => (
              <option key={c.categorie.id} value={c.categorie.id}>
                {c.categorie.libelle}
              </option>
            ))}
          </Selection>
        </Champ>

        {categorie && (
          <Champ label="Type d’absence" requis>
            <div className="flex items-center gap-2">
              <Selection value={typeId ?? ''} onChange={(e) => setTypeId(e.target.value || null)}>
                <option value="">Choisir</option>
                {categorie.types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.libelle}
                  </option>
                ))}
              </Selection>
              {type && <AideType type={type} />}
            </div>
          </Champ>
        )}
      </div>

      {mecanismes.includes('mention_legale_conge_principal') && mention?.value && (
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-nuit/80">
          {mention.value}
        </p>
      )}
      {mecanismes.includes('non_remunere') && (
        <p className="rounded-lg border border-jaune-fond/60 bg-white p-3 text-sm text-jaune">
          Cette absence n’est <strong>pas rémunérée</strong>.
        </p>
      )}

      {type && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Champ label="Du" requis>
              <Saisie
                type="date"
                value={debut}
                onChange={(e) => {
                  setDebut(e.target.value);
                  if (!fin || fin < e.target.value) setFin(e.target.value);
                }}
              />
            </Champ>
            <Champ label="Au" requis>
              <Saisie type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} />
            </Champ>
          </div>

          {/* La demi-journée ne se propose que sur UN SEUL JOUR : sur une
              période, les bornes sont des journées entières, et une demi-journée
              se pose comme une demande à part. */}
          {surUnSeulJour && (
            <div className="rounded-lg border border-nuit/10 bg-white px-3">
              <Case
                label="C’est une demi-journée"
                checked={estDemiJournee}
                onChange={(e) => setEstDemiJournee(e.target.checked)}
              />

              {estDemiJournee && (
                <div className="flex flex-wrap gap-4 pb-2 pl-6 text-sm text-nuit">
                  {(
                    [
                      ['matin', 'Matin'],
                      ['apres_midi', 'Après-midi'],
                    ] as const
                  ).map(([valeur, libelle]) => (
                    // `min-h-11` : la cible tactile fait 44 px, comme `Case`.
                    <label key={valeur} className="flex min-h-11 items-center gap-2">
                      <input
                        type="radio"
                        name="moitie-de-journee"
                        checked={moitie === valeur}
                        onChange={() => setMoitie(valeur)}
                        className="h-4 w-4 border-nuit/30 text-nuit focus:ring-ciel"
                      />
                      {libelle}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {verdict && (
            <p className="rounded-lg bg-ciel/20 p-3 text-sm text-nuit">
              {/* Dire la VÉRITÉ sur ce qui se passe : sans compteur derrière,
                  « décompté » laisse croire à une ponction sur un solde de
                  congés, alors que la retenue est sur le salaire. */}
              {verdict.compteur ? (
                <>
                  <strong>{jours(verdict.nbJours)} jour(s)</strong> décompté(s) de votre compteur
                  (week-ends et jours fériés exclus).
                </>
              ) : verdict.nonRemunere ? (
                <>
                  <strong>{jours(verdict.nbJours)} jour(s)</strong> d’absence sans solde (week-ends
                  et jours fériés exclus). Ces jours ne sont décomptés d’aucun compteur de congés ;
                  ils donnent lieu à une retenue correspondante sur votre salaire.
                </>
              ) : (
                <>
                  <strong>{jours(verdict.nbJours)} jour(s)</strong> d’absence (week-ends et jours
                  fériés exclus). Ces jours ne sont décomptés d’aucun compteur de congés.
                </>
              )}
              {verdict.aPosteriori && (
                <span className="mt-1 block text-jaune">
                  Cette absence a déjà commencé : votre manager et la RH en seront prévenus
                  immédiatement, et elle sera marquée « à régulariser en paie ».
                </span>
              )}
            </p>
          )}

          <Champ
            label="Description"
            requis={mecanismes.includes('description_obligatoire')}
            aide={
              mecanismes.includes('description_obligatoire')
                ? 'Sans compteur pour l’arbitrer, c’est la seule trace de ce qui est pris.'
                : undefined
            }
          >
            <Saisie value={description} onChange={(e) => setDescription(e.target.value)} />
          </Champ>

          {/* Le JUSTIFICATIF DIFFÉRÉ fait partie de cette liste. Il en avait été
              oublié : le champ disparaissait des deux types concernés, et un
              salarié qui avait DÉJÀ sa pièce ne pouvait plus la joindre — alors
              que le serveur savait la prendre. Relevé à l'audit du 01/10/2026. */}
          {(mecanismes.includes('justificatif_obligatoire') ||
            mecanismes.includes('justificatif_facultatif') ||
            mecanismes.includes('justificatif_differe')) && (
            <Champ
              label="Justificatif"
              requis={mecanismes.includes('justificatif_obligatoire')}
              aide={
                mecanismes.includes('justificatif_differe')
                  ? 'Si vous l’avez déjà, joignez-le. Sinon, déposez-le dès que vous l’obtenez : vous serez relancé après l’absence.'
                  : mecanismes.includes('justificatif_facultatif')
                    ? 'Facultatif : vous pourrez le déposer plus tard si la RH le demande.'
                    : undefined
              }
            >
              <input
                type="file"
                accept="image/*,application/pdf"
                className="block w-full text-sm text-nuit/70 file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-ciel/30 file:px-4 file:text-sm file:font-medium file:text-nuit"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) lireFichier(f, e.target);
                }}
              />
              {piece && <span className="mt-1 block text-xs text-nuit/60">{piece.filename}</span>}
            </Champ>
          )}

          <Champ label="Commentaire">
            <Saisie value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </Champ>
        </>
      )}

      {verdict && verdict.alertes.length > 0 && (
        <div className="rounded-lg border border-jaune-fond/60 bg-white p-3 text-sm text-jaune">
          {verdict.alertes.map((a, i) => (
            <p key={i}>{a}</p>
          ))}
        </div>
      )}

      {verdict && verdict.blocages.length > 0 && !seulBlocageSolde && (
        <div className="rounded-lg border border-corail-fond/60 bg-white p-3 text-sm text-corail">
          {verdict.blocages.map((b, i) => (
            <p key={i}>{b}</p>
          ))}
        </div>
      )}

      {verdict?.anticipation && !dedoublementPropose && (
        <div className="rounded-lg border border-corail-fond/60 border-l-4 border-l-corail-fond bg-white p-3 text-sm text-nuit/80">
          <p className="font-medium text-corail">Votre solde ne permet pas cette demande.</p>
          <p className="mt-1">
            Au {dateFr(debut)}, vous aurez{' '}
            <strong>{jours(Math.max(0, verdict.anticipation.soldeProjete))} jour(s)</strong> de
            congés payés acquis, pour {jours(verdict.nbJours)} demandé(s). Il n’y a donc rien à
            découper en congés payés.
          </p>
          <p className="mt-1 text-xs text-nuit/60">
            Vous pouvez poser tout ou partie de cette période en congé sans solde, ou attendre
            d’avoir acquis les jours nécessaires.
          </p>
        </div>
      )}

      {seulBlocageSolde && verdict?.dedoublement && (
        <div className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3">
          <p className="text-sm font-medium text-nuit">
            Votre solde ne couvre pas toute la période.
          </p>
          <p className="mt-1 text-sm text-nuit/80">
            Au {dateFr(debut)}, vous aurez{' '}
            <strong>{jours(verdict.anticipation!.soldeProjete)} jour(s)</strong> de congés payés
            acquis, pour {jours(verdict.nbJours)} demandé(s).
          </p>
          <ul className="mt-2 space-y-1 text-sm text-nuit/80">
            <li>
              <strong>{jours(verdict.dedoublement.joursAcquis)} j</strong> en congés payés, du{' '}
              {dateFr(verdict.dedoublement.cp.debut)} au {dateFr(verdict.dedoublement.cp.fin)}
              {verdict.dedoublement.cp.finDemi === 'matin' ? ' (matin)' : ''}
            </li>
            <li>
              <strong>{jours(verdict.dedoublement.joursSansSolde)} j</strong> en congé sans solde, du{' '}
              {dateFr(verdict.dedoublement.sansSolde.debut)}
              {verdict.dedoublement.sansSolde.debutDemi === 'apres_midi' ? ' (après-midi)' : ''} au{' '}
              {dateFr(verdict.dedoublement.sansSolde.fin)}
            </li>
          </ul>
          <p className="mt-2 text-xs text-nuit/60">
            Deux demandes distinctes seront créées. Elles suivent chacune leur circuit : l’une peut
            être acceptée et l’autre refusée.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <BoutonPrincipal
              type="button"
              onClick={() => dedoubler.mutate()}
              disabled={dedoubler.isPending}
            >
              {dedoubler.isPending ? 'Création…' : 'Accepter le découpage'}
            </BoutonPrincipal>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* La raison vit À CÔTÉ du bouton : plus haut dans la page, elle se
            perd, et c'est en butant sur le bouton qu'on se pose la question. */}
        {raisonBlocage && (
          <p className="mr-auto max-w-xl text-sm text-corail">{raisonBlocage}</p>
        )}
        <BoutonSecondaire type="button" onClick={onAnnuler}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal
          type="button"
          onClick={() => envoyer.mutate()}
          disabled={!peutEnvoyer || envoyer.isPending}
          title={raisonBlocage ?? undefined}
        >
          {envoyer.isPending ? 'Envoi…' : 'Envoyer la demande'}
        </BoutonPrincipal>
      </div>
    </div>
  );
}
